import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { DateTimeDisplay } from 'ui-components/components/date-time-display/date-time-display';

import type { LtlShipment, LtlAddress } from '@shipstation/client-common/types/ltl';
import { confirmationCardMessages } from './confirmation-card-messages';

import style from './confirmation-cards.module.less';

type ShipmentDetailsCardProps = {
  shipmentDetails: LtlShipment;
};

const formatAddressLines = (address: LtlAddress | null): string[] => {
  if (!address) return [];
  const lines: string[] = [];
  if (address.company) lines.push(address.company);
  if (address.line1) lines.push(address.line1);
  if (address.line2) lines.push(address.line2);
  const cityStateZip = [address.city, address.state].filter(Boolean).join(', ');
  if (cityStateZip || address.postalCode) {
    lines.push(`${cityStateZip} ${address.postalCode}`.trim());
  }
  return lines;
};

export const ShipmentDetailsCard = ({ shipmentDetails }: ShipmentDetailsCardProps) => {
  const intl = useIntl();

  const originLines = useMemo(() => formatAddressLines(shipmentDetails.originAddress ?? null), [shipmentDetails.originAddress]);
  const destinationLines = useMemo(
    () => formatAddressLines(shipmentDetails.destinationAddress ?? null),
    [shipmentDetails.destinationAddress],
  );

  return (
    <div className={style['card']}>
      <div className={style['card-header']}>
        <Text isBold textType={TextType.H4} value={confirmationCardMessages.shipmentDetailsHeader} />
      </div>
      <div className={style['card-body']}>
        <FlexColumn gap={Gap.Space12}>
          {originLines.length > 0 && (
            <FlexColumn className={style['address-block']} gap={Gap.Space4}>
              <Text className={style['detail-label']} textType={TextType.Label} value={confirmationCardMessages.originLabel} />
              {originLines.map((line, i) => (
                <Text key={i} textType={TextType.Paragraph} value={line} />
              ))}
            </FlexColumn>
          )}
          {destinationLines.length > 0 && (
            <FlexColumn className={style['address-block']} gap={Gap.Space4}>
              <Text className={style['detail-label']} textType={TextType.Label} value={confirmationCardMessages.destinationLabel} />
              {destinationLines.map((line, i) => (
                <Text key={i} textType={TextType.Paragraph} value={line} />
              ))}
            </FlexColumn>
          )}
          <FlexColumn gap={Gap.Space4}>
            <Text className={style['detail-label']} textType={TextType.Label} value={confirmationCardMessages.shipDateLabel} />
            <DateTimeDisplay utcTimestamp={shipmentDetails.shipDate} />
          </FlexColumn>
          {shipmentDetails.estimatedDeliveryDate && (
            <FlexColumn gap={Gap.Space4}>
              <Text className={style['detail-label']} textType={TextType.Label} value={confirmationCardMessages.estimatedDeliveryLabel} />
              <DateTimeDisplay utcTimestamp={shipmentDetails.estimatedDeliveryDate} />
            </FlexColumn>
          )}
          <FlexColumn gap={Gap.Space4}>
            <Text className={style['detail-label']} textType={TextType.Label} value={confirmationCardMessages.transitDaysLabel} />
            <Text
              textType={TextType.Paragraph}
              value={intl.formatMessage(confirmationCardMessages.transitDaysValue, { days: shipmentDetails.transitDays })}
            />
          </FlexColumn>
        </FlexColumn>
      </div>
    </div>
  );
};
